import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import { Skeleton } from "primereact/skeleton";
import ShareHolderSideBar from "./ShareHolderSideBar";
import Layout from "~/Layout";
import apiService from "~/services/service";
import API_ROUTES_V2 from "~/api-routes-v2";

const ShareHolderContext = createContext();

const ShareHolderDetail = () => {
  const { entityId, shareholderId } = useParams<{
    entityId: string;
    shareholderId: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [regData, setRegData] = useState({});
  const [loading, setLoading] = useState(true);
  let initialOfficerData = useRef({});

  useEffect(() => {
    fetchOfficerData();
  }, []);
  const fetchEntityDetails = async (id: string) => {
    try {
      const response = await apiService(API_ROUTES_V2.FETCH_ENTITY, "GET", {
        replace: { id },
      });
      return response;
    } catch (error) {
      throw new Error("Failed to fetch entity details");
    }
  };
  const fetchOfficerData = async () => {
    setLoading(true);
    // If navigation included skipEntityFetch flag (matching flow), avoid entity-scoped fetch
    const skipEntityFetch =
      (location &&
        (location as any).state &&
        (location as any).state.skipEntityFetch) ||
      false;

    let data: any = null;
    try {
      if (skipEntityFetch) {
        const respFallback = await apiService(
          API_ROUTES_V2.FETCH_SHAREHOLDER,
          "GET",
          {
            replace: { id: shareholderId, entity_id: entityId },
          },
        );
        data = respFallback?.data;
      } else {
        try {
          const resp = await apiService(
            API_ROUTES_V2.FETCH_SHAREHOLDER,
            "GET",
            {
              replace: { id: shareholderId, entity_id: entityId },
            },
          );
          data = resp?.data;
        } catch (err) {
          // If entity-scoped fetch fails (not linked yet), fallback to non-entity fetch
          const respFallback = await apiService(
            API_ROUTES_V2.FETCH_SHAREHOLDER,
            "GET",
            {
              replace: { id: shareholderId, entity_id: entityId },
            },
          );
          data = respFallback?.data;
        }
      }

      // Use entity ID from URL params to fetch entity details for sidebar
      const entityResponse = await fetchEntityDetails(entityId!);

      setRegData({ ...data, entityData: { ...entityResponse?.data } });
      initialOfficerData.current = {
        ...data,
        entityData: { ...entityResponse?.data },
      };
    } catch (error) {
      console.error("Failed to fetch shareholder data:", error);
      setRegData({});
    } finally {
      setLoading(false);
    }
  };

  const resetRegData = (data = {}) => {
    const mergedData = {
      ...initialOfficerData.current,
      ...data,
    };
    setRegData(mergedData);
  };

  // Update both regData state and the initialOfficerData ref
  // so that resetRegData won't revert to stale data
  const updateAndPersistRegData = (updates) => {
    setRegData((prev) => {
      const newData = { ...prev, ...updates };
      initialOfficerData.current = {
        ...initialOfficerData.current,
        ...updates,
      };
      return newData;
    });
  };

  const breadcrumbs = [
    {
      label: <span className="breadcrumb-link">My Entities</span>,
      command: () => navigate("/entities"),
      id: "entities",
    },
    {
      label: (
        <div className="flex">
          {!loading && regData?.entityData ? (
            <span className="breadcrumb-link">
              {regData.entityData.name} (
              <ReactCountryFlag
                countryCode={regData.entityData.country}
                svg
              />{" "}
              {getName(regData.entityData.country)})
            </span>
          ) : (
            <>
              <Skeleton width="100px" height="20px" className="mr-1" />
              <Skeleton width="150px" height="20px" className="mr-1" />
            </>
          )}
        </div>
      ),
      command: () =>
        regData?.entityData &&
        navigate(`/entity/${regData.entityData.id}/dashboard`),
      id: "entity",
    },
    {
      label: <span className="breadcrumb-link">Appointments & Ownership</span>,
      command: () =>
        regData?.entityData &&
        navigate(
          `/entity/${regData.entityData.id}/appointments-ownership?tab=shareholders`,
        ),
      id: "shareholder-data",
    },
    {
      label: (
        <>
          {!loading && regData ? (
            regData?.type === "Individual" ? (
              regData?.preferred_name ||
              `${regData?.name} ${regData?.last_name}`
            ) : (
              `${regData?.legal_name}`
            )
          ) : (
            <Skeleton width="100px" height="20px" className="mr-1" />
          )}
        </>
      ),
      id: "shareholder",
    },
  ];

  return (
    <ShareHolderContext.Provider
      value={{
        regData,
        setRegData,
        resetRegData,
        updateAndPersistRegData,
        loading,
      }}
    >
      <Layout crumbs={breadcrumbs}>
        <div className="grid grid-nogutter">
          <div className="col-12 Entities-container">
            <div className="grid">
              <div className="col-3">
                <ShareHolderSideBar />
              </div>
              <div className="col-9">
                <Outlet />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </ShareHolderContext.Provider>
  );
};

export default ShareHolderDetail;
export const useShareHolderDetails = () => useContext(ShareHolderContext);
